import { ServerDTO } from '../../models/app.model';
import { compose } from '@ngrx/store';

/**
 * App system helper (common)
 *
 * @author Mamontov Andrey, 03/2018
 *
 */

export class HelperSystem {

  /**
   * Deep object clone
   * @see https://stackoverflow.com/questions/28150967/typescript-cloning-object
   *
   * @param obj
   * @return {any}
   */
  static deepObjClone(obj: any): any {

    let copy;
    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
      return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepObjClone(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepObjClone(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error('Unable to copy obj! Its type isn\'t supported.');
  }

  /**
   * Simple clone object (don't use for objects with methods, array as member)
   *
   * @param obj
   * @returns {any}
   */
  static simpleClone(obj: any): any {
    return Object.assign({}, obj);
  }

  /**
   * Generate uuid
   *
   * @return {string}
   */
  static generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Sort data
   *
   * @param {string} key
   * @param {string} order
   */
  static sortDataByKey(key: string, order: string = 'asc') {
    return function(a, b) {
      if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
        // property doesn't exist on either object
        return 0;
      }

      const varA = (typeof a[key] === 'string') ?
        a[key].toUpperCase() : a[key];
      const varB = (typeof b[key] === 'string') ?
        b[key].toUpperCase() : b[key];

      let comparison = 0;
      if (varA > varB) {
        comparison = 1;
      } else if (varA < varB) {
        comparison = -1;
      }
      return (
        (order === 'desc') ? (comparison * -1) : comparison
      );
    };
  }

  /**
   * Returns difference of arrays
   * @see https://stackoverflow.com/a/21988249/1095357
   *
   * @param {Array} origA
   * @param {Array} existA
   * @return {Array}
   */
  static arrayDiff(origA: Array<any>, existA: Array<any>): Array<any> {

    if (existA.length === 0) {
      return origA;
    }

    // Find values that are in origA but not in existA
    const uniqueResultOne = origA.filter(function(item) {
      return !existA.some(function(item2) {
        return item.name === item2.name;
      });
    });

    // Find values that are in existA but not in origA
    const uniqueResultTwo = existA.filter(function(item) {
      return !origA.some(function(item2) {
        return item.name === item2.name;
      });
    });

    // Combine the two arrays of unique entries
    return uniqueResultOne.concat(uniqueResultTwo);
  }

  static mergeArray(origA: Array<any>, addedA: Array<any>) {
    return [origA, addedA].reduce((a, b) => a.map((c, i) => Object.assign({}, c, b[i])));
  }

  static transliterate(text: string) {
    const a = {'Ё': 'YO', 'Й': 'I',   'Ц': 'TS',  'У': 'U',  'К': 'K',  'Е': 'E',  'Н': 'N',  'Г': 'G',  'Ш': 'SH',
      'Щ': 'SCH',  'З': 'Z',  'Х': 'H',  'Ъ': '',  'ё': 'yo',  'й': 'i',  'ц': 'ts',  'у': 'u',  'к': 'k',  'е': 'e',  'н': 'n',
      'г': 'g',  'ш': 'sh',  'щ': 'sch',  'з': 'z',
      'х': 'h',  'ъ': '', 'Ф': 'F',    'Ы': 'I',    'В': 'V',    'А': 'a',  'П': 'P',  'Р': 'R',
      'О': 'O',  'Л': 'L',  'Д': 'D',  'Ж': 'ZH',  'Э': 'E',  'ф': 'f',  'ы': 'i',  'в': 'v',
      'а': 'a',  'п': 'p',  'р': 'r',  'о': 'o',  'л': 'l',  'д': 'd',  'ж': 'zh',  'э': 'e',
      'Я': 'Ya',  'Ч': 'CH',  'С': 'S',  'М': 'M',  'И': 'I',  'Т': 'T',  'Ь': '',  'Б': 'B',
      'Ю': 'YU',  'я': 'ya',  'ч': 'ch',  'с': 's',  'м': 'm',  'и': 'i',  'т': 't',  'ь': '',  'б': 'b',  'ю': 'yu'};

    return text.split('').map( char => {
        char.trim();
        return a[char] || char;
    }).join('');
  }

  /**
   * Round a float value to value with fixed part (after the point)
   *
   * @param number
   * @param precision
   * @return number
   */
  static toFixed(number: number, precision: number = 2): number {
    return Number(number.toFixed(precision));
  }

  /**
   * Remove doubles from an array by a key
   *
   * @param arr
   * @param uniqueKeyName
   * @return array
   */
  static removeDuplicatesFromObjectsArray(arr: Array<any>, uniqueKeyName: string = 'id') {
    const seen = new Set();
    const filteredArr = arr.filter(el => {
      const duplicate = seen.has(el[uniqueKeyName]);
      seen.add(el[uniqueKeyName]);
      return !duplicate;
    });
    return filteredArr;
  }

}

/**
 * Various useful things
 */

/**
 * Return DTO object as an array of a type
 * @param x ServerDTO
 */
export const dtoFormatter = <T extends {}>(x: ServerDTO): Array<T> => x && x.rows ? x.rows as Array<T> : [];

/**
 * Type caster
 * convert any type to needed type
 * @param x
 */
export const typeCaster = <T extends {}>(x: any): T => x as T;

/**
 * Router slicer
 */
const routerSlicer = (s: string) => s.length > 0 && s.indexOf('/') !== -1 ? s.split('/').slice(2) : [];
const routerCombiner = (r: Array<string>) => r.length > 0 ? r.join('/') : '';
// get url prefix from url without :vpo params defined earlier
export const urlPrefix = compose(routerCombiner, routerSlicer);

export const structPrefix = (s: string, id: string) => id ? s.substring(0, s.indexOf(`/${id}`)) : s;

/**
 * Map to IAlbum interface (Lightbox)
 * @param i (is expected to be DocumentImage interface)
 */
export const albumMap = (i): any => ({ src: i.image, caption: '', thumb: ''});
